import type {
  BenchmarkKey,
  BenchmarkMonitoring,
  MonitoringCell,
  MonitoringCounts,
  MonitoringGroup,
  MonitoringStudent,
  ScoredBy,
} from './types';
import { SHOWN_BENCHMARK_KEYS } from './types';

export const BENCHMARKS_ROUTE = '/benchmarks';

export function schoolRoute(schoolId: string): string {
  return `${BENCHMARKS_ROUTE}/schools/${encodeURIComponent(schoolId)}`;
}

export function teacherRoute(schoolId: string, teacherId: string): string {
  return `${schoolRoute(schoolId)}/teachers/${encodeURIComponent(teacherId)}`;
}

function shown(key: BenchmarkKey): boolean {
  return SHOWN_BENCHMARK_KEYS.includes(key);
}

function shownGroups(groups: MonitoringGroup[] | undefined): MonitoringGroup[] | undefined {
  return groups?.map((g) => ({ ...g, benchmarks: g.benchmarks.filter((b) => shown(b.key)) }));
}

export function withoutHiddenBenchmarks(data: BenchmarkMonitoring): BenchmarkMonitoring {
  return {
    ...data,
    benchmarks: data.benchmarks.filter((b) => shown(b.key)),
    schools: shownGroups(data.schools),
    teachers: shownGroups(data.teachers),
    studentRows: data.studentRows?.map((s) => ({ ...s, cells: s.cells.filter((c) => shown(c.key)) })),
  };
}

export type WindowMode = { open: boolean; flashlightScored: boolean };

export function windowMode(data: Pick<BenchmarkMonitoring, 'window'>): WindowMode {
  return { open: data.window?.state === 'open', flashlightScored: data.window?.flashlightScored === true };
}

// FL360 stores a window's start and close as calendar dates written in UTC
// (midnight and 23:59:59Z), so those are read by their UTC date; a
// submission time is a real instant and is read in the viewer's time zone.
export function boundaryDate(iso: string): Date {
  const d = new Date(iso);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

export function formatDay(iso: string): string {
  return boundaryDate(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatMonthDay(iso: string): string {
  return boundaryDate(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

export function calendarDaysBetween(now: Date, iso: string): number {
  return Math.round((startOfDay(new Date(iso)) - startOfDay(now)) / DAY_MS);
}

export function daysUntilBoundary(now: Date, iso: string): number {
  return Math.round((boundaryDate(iso).getTime() - startOfDay(now)) / DAY_MS);
}

export function statusLabels({ open, flashlightScored }: WindowMode): [string, string, string, string] {
  const waiting = flashlightScored ? 'Being scored' : 'To score';
  return open
    ? ['Scored', waiting, 'In progress', 'Not started']
    : ['Scored', waiting, "Didn't finish", 'Never started'];
}

function percent(part: number, base: number): number {
  return base === 0 ? 0 : Math.round((part / base) * 100);
}

export function percentLine(counts: MonitoringCounts): string {
  const submitted = percent(counts.scored + counts.toScore, counts.base);
  const scored = percent(counts.scored, counts.base);
  return `${submitted}% submitted · ${scored}% scored`;
}

export function scoredByText(scoredBy: ScoredBy): string {
  switch (scoredBy) {
    case 'flashlight':
      return 'Scored by Flashlight';
    case 'auto':
      return 'Scored automatically';
    default:
      return 'Scored by teachers';
  }
}

export function hotNotStarted(counts: MonitoringCounts): boolean {
  return counts.base > 0 && counts.notStarted / counts.base >= 0.2;
}

function scoredShare(group: MonitoringGroup, key: BenchmarkKey): number {
  const row = group.benchmarks.find((b) => b.key === key);
  if (!row || row.counts.base === 0) return 1;
  return row.counts.scored / row.counts.base;
}

export function sortLeastCompleteFirst(groups: MonitoringGroup[], key: BenchmarkKey): MonitoringGroup[] {
  return [...groups].sort(
    (a, b) => scoredShare(a, key) - scoredShare(b, key) || a.ref.name.localeCompare(b.ref.name),
  );
}

function unfinished(student: MonitoringStudent): boolean {
  return student.cells.some((c) => (c.state === 'inProgress' || c.state === 'notStarted') && !c.closed);
}

export function waitingDays(cell: MonitoringCell, now: Date): number | undefined {
  if (cell.state !== 'toScore' || !cell.submittedAt) return undefined;
  return Math.max(0, -calendarDaysBetween(now, cell.submittedAt));
}

function longestWait(student: MonitoringStudent, now: Date): number {
  return Math.max(-1, ...student.cells.map((c) => waitingDays(c, now) ?? (c.state === 'toScore' ? 0 : -1)));
}

function allScored(student: MonitoringStudent): boolean {
  return (
    student.cells.some((c) => c.state === 'scored') &&
    student.cells.every((c) => c.state === 'scored' || c.state === 'off' || c.state === 'notAssigned')
  );
}

function rank(student: MonitoringStudent): number {
  if (unfinished(student)) return 0;
  if (allScored(student)) return 2;
  return 1;
}

export function sortMostLeftToDoFirst(students: MonitoringStudent[], now: Date): MonitoringStudent[] {
  return [...students].sort((a, b) => {
    const byRank = rank(a) - rank(b);
    if (byRank !== 0) return byRank;
    if (rank(a) === 1) {
      const byWait = longestWait(b, now) - longestWait(a, now);
      if (byWait !== 0) return byWait;
    }
    return a.name.localeCompare(b.name);
  });
}

export function splitScoredStudents(students: MonitoringStudent[]): {
  todo: MonitoringStudent[];
  done: MonitoringStudent[];
} {
  return {
    todo: students.filter((s) => !allScored(s)),
    done: students.filter(allScored),
  };
}

function days(n: number): string {
  return `${n} ${n === 1 ? 'day' : 'days'}`;
}

export function cellText(cell: MonitoringCell, { open, flashlightScored }: WindowMode, now: Date): string {
  switch (cell.state) {
    case 'scored':
      return 'Scored';
    case 'off':
      return 'Off';
    case 'notAssigned':
      return 'Not assigned';
    case 'toScore': {
      const label = (cell.scoredByFlashlight ?? flashlightScored) ? 'Being scored' : 'To score';
      const waited = waitingDays(cell, now);
      return waited === undefined ? label : `${label} · ${days(waited)}`;
    }
    case 'inProgress':
      if (!open || cell.closed) return "Didn't finish";
      return cell.step ? `In progress · ${cell.step}` : 'In progress';
    default:
      return open && !cell.closed ? 'Not started' : 'Never started';
  }
}

// While a window is open, part of a row's In progress or Not started can sit
// on a variant that has already closed; the drill-down reads those as
// "Didn't finish" and "Never started", so the aggregate says so too.
export function closedNote(counts: MonitoringCounts, column: 'inProgress' | 'notStarted', open: boolean): string | undefined {
  if (!open) return undefined;
  const closed = column === 'inProgress' ? counts.closedInProgress ?? 0 : counts.closedNotStarted ?? 0;
  if (closed === 0) return undefined;
  return column === 'inProgress' ? `${closed} didn't finish` : `${closed} never started`;
}

export function copyEmailsLabel(count: number, open: boolean): string {
  const teachers = `${count} ${count === 1 ? 'teacher' : 'teachers'}`;
  return `Copy emails · ${teachers} with ${open ? 'work left' : 'work to score'}`;
}

export type TileHeadline = { number?: string; line: string; date: string };

// Null only when nothing is scheduled this school year at all.
export function tileHeadline(data: BenchmarkMonitoring, now: Date): TileHeadline | null {
  if (data.window && data.window.state === 'open') {
    const left = Math.max(0, daysUntilBoundary(now, data.window.closeAt));
    return {
      number: String(left),
      line: `${left === 1 ? 'day' : 'days'} left in the ${data.window.label} window`,
      date: `Closes ${formatDay(data.window.closeAt)}`,
    };
  }
  const next = data.upNext[0];
  if (!next) {
    if (!data.window) return null;
    return {
      line: `${data.window.label} window closed ${formatDay(data.window.closeAt)}`,
      date: 'Next window not scheduled',
    };
  }
  const until = Math.max(0, daysUntilBoundary(now, next.startAt));
  return {
    number: String(until),
    line: `${until === 1 ? 'day' : 'days'} until ${next.label} opens`,
    date: `Opens ${formatDay(next.startAt)}`,
  };
}

export function windowHeading(data: Pick<BenchmarkMonitoring, 'window'>): string {
  if (!data.window) return 'Benchmarks';
  return data.window.state === 'open' ? 'Benchmarks in this window' : `${data.window.label} results`;
}

export type Crumb = { label: string; to?: string };

function windowCrumbLabel(data: Pick<BenchmarkMonitoring, 'window'>): string {
  return data.window?.state === 'open' ? `${data.window.label} benchmarks` : 'Benchmarks';
}

export function breadcrumbsFor(data: BenchmarkMonitoring): Crumb[] {
  const crumbs: Crumb[] = [{ label: 'Dashboard', to: '/' }];
  const schoolScoped = data.scope === 'school';
  if (!schoolScoped) {
    crumbs.push({ label: windowCrumbLabel(data), to: data.level === 'district' ? undefined : BENCHMARKS_ROUTE });
  }
  if (data.school && data.level !== 'district') {
    const schoolTo = schoolScoped ? BENCHMARKS_ROUTE : schoolRoute(data.school.id);
    crumbs.push({ label: data.school.name, to: data.level === 'teacher' ? schoolTo : undefined });
  }
  if (data.teacher && data.level === 'teacher') {
    crumbs.push({ label: data.teacher.name });
  }
  return crumbs;
}

export type PageHeader = { title: string; subtitle: string };

export function pageHeaderFor(data: BenchmarkMonitoring, now: Date): PageHeader {
  const w = data.window;
  if (data.level === 'teacher' && data.teacher) {
    const schoolName = data.school?.name ?? '';
    const windowText = w
      ? `${w.label} window ${w.state === 'open' ? 'closes' : 'closed'} ${formatDay(w.closeAt)}`
      : `${data.schoolYear} school year`;
    return { title: data.teacher.name, subtitle: `${schoolName} · ${windowText}` };
  }
  if (data.level === 'school' && data.school) {
    let subtitle = `${data.schoolYear} school year`;
    if (w && w.state === 'open') {
      subtitle = `${w.label} benchmark window · closes ${formatDay(w.closeAt)} · ${days(Math.max(0, daysUntilBoundary(now, w.closeAt)))} left`;
    } else if (w) {
      const next = data.upNext[0];
      subtitle = `${w.label} window closed ${formatDay(w.closeAt)}`;
      if (next) subtitle += ` · ${next.label} opens ${formatDay(next.startAt)}`;
    }
    return { title: data.school.name, subtitle };
  }
  if (w && w.state === 'open') {
    return {
      title: `${w.label} benchmark window`,
      subtitle: `${data.districtName} · ${formatMonthDay(w.startAt)} to ${formatDay(w.closeAt)} · ${days(Math.max(0, daysUntilBoundary(now, w.closeAt)))} left`,
    };
  }
  return { title: 'Benchmarks', subtitle: `${data.districtName} · ${data.schoolYear} school year` };
}
